import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { 
  ArrowLeft, 
  Save, 
  Plus, 
  Trash2, 
  Layers, 
  BookOpen, 
  Scale, 
  Info,
  Loader2,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Zap,
  CheckCircle2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useApiClient } from "@/hooks/use-api-client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface PlaybookTopicRule {
  text: string;
  source_reference?: string;
}

interface PlaybookTopic {
  id: string;
  name: string;
  description?: string;
  rules: PlaybookTopicRule[];
}

interface PlaybookSkill {
  id: string;
  name: string;
  description?: string;
  topics: PlaybookTopic[];
}

export default function EditSkill() {
  const { skillId } = useParams();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [skill, setSkill] = useState<PlaybookSkill | null>(null);
  const [expandedTopics, setExpandedTopics] = useState<Record<string, boolean>>({});

  const fetchSkill = useCallback(async () => {
    if (!skillId) return;
    setIsLoading(true);
    try {
      const res = await apiClient.get(`/api/v1/contract-review/skills/${skillId}`);
      if (res.ok) {
        setSkill(res.data.skill);
        // Expand all topics by default
        const initialExpanded: Record<string, boolean> = {};
        res.data.skill.topics.forEach((t: PlaybookTopic) => {
          initialExpanded[t.id] = true;
        });
        setExpandedTopics(initialExpanded);
      } else {
        toast.error("No se pudo cargar el skill.");
        navigate("/dashboard/skills");
      }
    } catch (err) {
      console.error("Error fetching skill:", err);
      toast.error("Error de conexión.");
    } finally {
      setIsLoading(false);
    }
  }, [apiClient, skillId, navigate]);

  useEffect(() => {
    fetchSkill();
  }, [fetchSkill]);

  const handleSave = async () => {
    if (!skill || !skillId) return;
    setIsSaving(true);
    try {
      const res = await apiClient.put(`/api/v1/contract-review/skills/${skillId}`, {
        name: skill.name,
        description: skill.description,
        topics: skill.topics
      });
      if (res.ok) {
        toast.success("Skill actualizado correctamente");
        navigate("/dashboard/skills");
      } else {
        toast.error("Error al guardar los cambios");
      }
    } catch (err) {
      console.error("Error updating skill:", err);
      toast.error("Error de red");
    } finally {
      setIsSaving(false);
    }
  };

  const toggleTopic = (id: string) => {
    setExpandedTopics(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const updateTopic = (topicId: string, field: keyof PlaybookTopic, value: any) => {
    if (!skill) return;
    setSkill({
      ...skill,
      topics: skill.topics.map(t => t.id === topicId ? { ...t, [field]: value } : t)
    });
  };

  const addTopic = () => {
    if (!skill) return;
    const newTopic: PlaybookTopic = {
      id: Math.random().toString(36).substr(2, 9),
      name: "Nuevo Tema",
      description: "",
      rules: []
    };
    setSkill({
      ...skill,
      topics: [...skill.topics, newTopic]
    });
    setExpandedTopics(prev => ({ ...prev, [newTopic.id]: true }));
  };

  const removeTopic = (topicId: string) => {
    if (!skill) return;
    setSkill({
      ...skill,
      topics: skill.topics.filter(t => t.id !== topicId)
    });
  };

  const addRule = (topicId: string) => {
    if (!skill) return;
    setSkill({
      ...skill,
      topics: skill.topics.map(t => t.id === topicId ? {
        ...t,
        rules: [...t.rules, { text: "", source_reference: "" }]
      } : t)
    });
  };

  const updateRule = (topicId: string, ruleIdx: number, field: keyof PlaybookTopicRule, value: string) => {
    if (!skill) return;
    setSkill({
      ...skill,
      topics: skill.topics.map(t => t.id === topicId ? {
        ...t,
        rules: t.rules.map((r, idx) => idx === ruleIdx ? { ...r, [field]: value } : r)
      } : t)
    });
  };

  const removeRule = (topicId: string, ruleIdx: number) => {
    if (!skill) return;
    setSkill({
      ...skill,
      topics: skill.topics.map(t => t.id === topicId ? {
        ...t,
        rules: t.rules.filter((_, idx) => idx !== ruleIdx)
      } : t)
    });
  };

  if (isLoading) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-[#f8fafc]">
        <Loader2 className="h-12 w-12 text-purple-500 animate-spin mb-4" />
        <p className="text-slate-500 font-medium text-lg">Cargando biblioteca de conocimiento...</p>
      </div>
    );
  }

  if (!skill) return null;

  return (
    <div className="flex-1 flex flex-col bg-[#f8fafc] min-h-screen pb-20">
      {/* Sticky Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-gray-100 p-6 sticky top-0 z-20 shadow-sm">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate("/dashboard/skills")}
              className="text-slate-500 hover:text-slate-900 rounded-xl h-10 w-10 p-0"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <Badge variant="outline" className="bg-purple-50 text-purple-600 border-purple-100 text-[10px] font-bold uppercase tracking-wider">
                  Editor de Skill
                </Badge>
                <span className="text-slate-300">/</span>
                <span className="text-slate-500 text-xs font-medium">{skill.name}</span>
              </div>
              <h1 className="text-xl font-black text-slate-900">Perfeccionar Conocimiento</h1>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              onClick={() => navigate("/dashboard/skills")}
              className="rounded-xl font-bold border-slate-200 hidden md:flex"
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleSave}
              disabled={isSaving}
              className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-bold py-2 px-6 rounded-xl shadow-lg shadow-purple-200 transition-all hover:scale-105 min-w-[140px]"
            >
              {isSaving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              {isSaving ? "Guardando..." : "Guardar Cambios"}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto w-full p-8 mt-4 space-y-10">
        {/* Basic Info Section */}
        <section className="bg-white rounded-[2.5rem] p-10 border border-gray-100 shadow-xl shadow-slate-200/50 space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="flex items-center gap-4 mb-2">
            <div className="w-12 h-12 rounded-2xl bg-purple-100 flex items-center justify-center">
              <Info className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <h2 className="text-2xl font-black text-slate-900">Información General</h2>
              <p className="text-slate-500 text-sm">Define cómo se identificará este skill en tus agentes.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6">
            <div className="space-y-2">
              <Label htmlFor="skill-name" className="text-slate-700 font-bold ml-1">Nombre de la Habilidad</Label>
              <Input 
                id="skill-name"
                value={skill.name}
                onChange={(e) => setSkill({ ...skill, name: e.target.value })}
                placeholder="Ej: Playbook de Arrendamiento"
                className="py-6 px-5 bg-slate-50/50 border-gray-100 rounded-2xl text-lg font-bold focus:ring-purple-500 focus:bg-white transition-all"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="skill-desc" className="text-slate-700 font-bold ml-1">Descripción / Propósito</Label>
              <Textarea 
                id="skill-desc"
                value={skill.description}
                onChange={(e) => setSkill({ ...skill, description: e.target.value })}
                placeholder="Explica qué tipo de contratos audita este skill..."
                className="min-h-[100px] px-5 py-4 bg-slate-50/50 border-gray-100 rounded-2xl text-slate-600 focus:ring-purple-500 focus:bg-white transition-all"
              />
            </div>
          </div>
        </section>

        {/* Topics Section */}
        <div className="space-y-6">
          <div className="flex items-center justify-between px-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center text-amber-600">
                <Layers className="h-5 w-5" />
              </div>
              <h2 className="text-2xl font-black text-slate-900">Temas y Reglas</h2>
            </div>
            <Button 
              onClick={addTopic}
              className="bg-white text-purple-600 border-2 border-purple-100 hover:bg-purple-50 hover:border-purple-200 font-bold rounded-2xl py-5 px-6 shadow-sm transition-all"
            >
              <Plus className="mr-2 h-5 w-5" />
              Agregar Tema
            </Button>
          </div>

          <div className="space-y-6">
            {skill.topics.map((topic, tIdx) => (
              <div 
                key={topic.id} 
                className={cn(
                  "bg-white rounded-[2rem] border transition-all duration-300 overflow-hidden",
                  expandedTopics[topic.id] ? "border-purple-200 shadow-xl shadow-purple-500/5 ring-1 ring-purple-100" : "border-gray-100 shadow-sm"
                )}
              >
                {/* Topic Header */}
                <div 
                  className={cn(
                    "p-6 flex items-center justify-between cursor-pointer group",
                    expandedTopics[topic.id] ? "bg-purple-50/30 border-b border-purple-50" : "hover:bg-slate-50"
                  )}
                  onClick={() => toggleTopic(topic.id)}
                >
                  <div className="flex items-center gap-4 flex-1">
                    <div className={cn(
                      "w-10 h-10 rounded-xl flex items-center justify-center transition-colors",
                      expandedTopics[topic.id] ? "bg-purple-600 text-white" : "bg-slate-100 text-slate-400 group-hover:bg-slate-200"
                    )}>
                      {tIdx + 1}
                    </div>
                    <div className="flex-1">
                      {expandedTopics[topic.id] ? (
                        <Input 
                          value={topic.name}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => updateTopic(topic.id, 'name', e.target.value)}
                          className="font-bold text-lg bg-transparent border-none p-0 focus:ring-0 w-full"
                          placeholder="Nombre del tema..."
                        />
                      ) : (
                        <h3 className="font-bold text-slate-800 text-lg">{topic.name || "Tema sin nombre"}</h3>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={(e) => { e.stopPropagation(); removeTopic(topic.id); }}
                      className="h-9 w-9 rounded-xl text-slate-300 hover:text-red-500 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    <div className="h-9 w-9 rounded-xl flex items-center justify-center text-slate-400">
                      {expandedTopics[topic.id] ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                    </div>
                  </div>
                </div>

                {/* Topic Body */}
                {expandedTopics[topic.id] && (
                  <div className="p-8 space-y-8 animate-in slide-in-from-top-2 duration-300">
                    <div className="space-y-2">
                      <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Descripción del Tema</Label>
                      <Textarea 
                        value={topic.description}
                        onChange={(e) => updateTopic(topic.id, 'description', e.target.value)}
                        placeholder="Define qué abarca este tema..."
                        className="bg-slate-50/50 border-gray-100 rounded-xl text-sm focus:bg-white transition-all min-h-[80px]"
                      />
                    </div>

                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Reglas de Cumplimiento</Label>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => addRule(topic.id)}
                          className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 font-bold h-7 rounded-lg text-[11px]"
                        >
                          <Plus className="w-3 h-3 mr-1" /> Agregar Regla
                        </Button>
                      </div>

                      <div className="space-y-3">
                        {topic.rules.map((rule, rIdx) => (
                          <div key={rIdx} className="group flex gap-4 bg-slate-50/30 p-4 rounded-2xl border border-slate-100 relative hover:border-purple-100 transition-colors">
                            <div className="w-6 h-6 rounded-lg bg-white flex items-center justify-center text-[10px] font-black text-slate-400 border border-slate-100">
                              {rIdx + 1}
                            </div>
                            <div className="flex-1 space-y-3">
                              <Input 
                                value={rule.text}
                                onChange={(e) => updateRule(topic.id, rIdx, 'text', e.target.value)}
                                placeholder="Define la regla (ej: El plazo de pago no debe exceder 30 días)"
                                className="bg-transparent border-none p-0 focus:ring-0 font-medium text-slate-700 placeholder:text-slate-300"
                              />
                              <div className="flex items-center gap-2">
                                <Scale className="w-3 h-3 text-slate-300" />
                                <Input 
                                  value={rule.source_reference}
                                  onChange={(e) => updateRule(topic.id, rIdx, 'source_reference', e.target.value)}
                                  placeholder="Referencia (ej: Art. 4 Política Interna)"
                                  className="bg-transparent border-none p-0 focus:ring-0 text-[10px] uppercase font-bold tracking-wider text-slate-400 placeholder:text-slate-200 h-4"
                                />
                              </div>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => removeRule(topic.id, rIdx)}
                              className="h-8 w-8 rounded-lg text-slate-200 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all"
                            >
                              <XIcon className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                        {topic.rules.length === 0 && (
                          <div className="text-center py-6 border-2 border-dashed border-slate-100 rounded-2xl">
                            <p className="text-slate-400 text-sm">No hay reglas definidas para este tema.</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {skill.topics.length === 0 && (
            <div className="text-center py-20 bg-white rounded-[2.5rem] border border-dashed border-slate-200">
              <Layers className="w-16 h-16 text-slate-200 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-slate-800">Aún no hay temas</h3>
              <p className="text-slate-500 mb-6">Comienza agregando un tema para estructurar tu conocimiento.</p>
              <Button 
                onClick={addTopic}
                className="bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-xl"
              >
                <Plus className="mr-2 h-4 w-4" /> Agregar Primer Tema
              </Button>
            </div>
          )}
        </div>

        {/* Footer Guidance */}
        <div className="bg-indigo-50 border border-indigo-100 rounded-[2rem] p-8 flex gap-6 items-center">
          <div className="w-16 h-16 rounded-2xl bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
            <Sparkles className="h-8 w-8 text-indigo-600" />
          </div>
          <div>
            <h4 className="font-black text-indigo-900 text-lg mb-1">Consejo Pro</h4>
            <p className="text-indigo-800/70 leading-relaxed text-sm">
              Las reglas deben ser afirmativas y claras. En lugar de decir "Revisar los pagos", usa <strong>"Los pagos deben ser procesados en máximo 30 días calendario"</strong>. Esto ayuda a la IA a ser más precisa en la auditoría.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function XIcon(props: any) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  )
}
